import {
  CheckIconProps,
  Combobox,
  ComboboxItem,
  ComboboxProps,
  Flex,
  Group,
  Input,
  InputBase,
  ScrollArea,
  useCombobox,
} from "@mantine/core";
import { IconCaretDownFilled, IconCheck } from "@tabler/icons-react";
import { useMemo, useRef, useState } from "react";
import Avatar from "../Avatar";

const iconProps: CheckIconProps = {
  color: "currentColor",
  opacity: 0.6,
  size: 18,
};

type McMantineUserSelectNewProps = Omit<ComboboxProps, "onChange"> & {
  onChange: (value: string | null) => void;
  data: ComboboxItem[];
  value: string | null;
  placeholder?: string;
};

export function McMantineUserSelect({ data, onChange, value, placeholder, ...props }: McMantineUserSelectNewProps) {
  const [search, setSearch] = useState<string>("");
  const [hasOpenedOnce, setHasOpenedOnce] = useState<boolean>(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
    onDropdownOpen: (eventSource) => {
      setHasOpenedOnce(true);

      if (eventSource === "keyboard") {
        combobox.selectActiveOption();
      } else {
        combobox.updateSelectedOptionIndex("active");
      }

      setTimeout(() => searchRef.current?.focus(), 0);
    },
  });

  const currentItem = useMemo(() => data.find((entry) => entry.value === value) ?? null, [data, value]);

  const options = data
    .filter((item) => item.label.toLowerCase().includes(search.toLowerCase()))
    .map((item) => (
      <Combobox.Option value={item.value} key={item.value} active={item.value === value}>
        <Group flex="1" gap="xs" wrap="nowrap">
          <Avatar id={item.value} name={item.label} size="sm" />
          <Flex wrap="nowrap" flex={1}>
            {item.label}
          </Flex>
          {item.value === value && <IconCheck style={{ marginInlineStart: "auto" }} {...iconProps} />}
        </Group>
      </Combobox.Option>
    ));

  return (
    <Combobox
      store={combobox}
      resetSelectionOnOptionHover
      onOptionSubmit={(val) => {
        if (val === value) {
          onChange(null);
        } else {
          onChange(val);
        }
        combobox.closeDropdown();
      }}
      {...props}
    >
      <Combobox.Target>
        <InputBase
          styles={{
            input: {
              outline: "none",
              boxShadow: "none",
            },
          }}
          component="button"
          type="button"
          pointer
          rightSection={<IconCaretDownFilled size={17} color="var(--mc-icon-default)" />}
          rightSectionPointerEvents="none"
          onClick={() => combobox.toggleDropdown()}
        >
          {currentItem != null ? (
            <Group flex="1" gap="xs" wrap="nowrap" style={{ overflow: "hidden", whiteSpace: "nowrap" }}>
              <Avatar id={currentItem.value} name={currentItem.label} size="sm" />
              <Flex wrap="nowrap" flex={1}>
                {currentItem.label}
              </Flex>
            </Group>
          ) : (
            <Input.Placeholder>{placeholder ?? "Velg bruker"}</Input.Placeholder>
          )}
        </InputBase>
      </Combobox.Target>

      <Combobox.Dropdown>
        {data.length > 10 && (
          <Combobox.Search
            ref={searchRef}
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            placeholder="Søk..."
          />
        )}
        <Combobox.Options>
          {hasOpenedOnce && (
            <ScrollArea.Autosize mah={200} type="scroll">
              {options.length === 0 ? <Combobox.Empty>Ingen brukere funnet</Combobox.Empty> : options}
            </ScrollArea.Autosize>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
